import type { Component } from 'vue';
import type { ComponentProps } from 'vue-component-type-helpers';
import type { IsAny } from '#layers/base/public';
import { useOverlay } from '@nuxt/ui/composables';
import { DDialog } from '#components';

type OverlayResult<T extends Component> = NonNullable<Awaited<ReturnType<typeof _inferOverlayResult<T>>['result']>>;
type ConfirmPayload<T extends Component> = NonNullable<Exclude<OverlayResult<T>, false>>;

/**
 * Type constraint that checks if a component has a 'close' emit event.
 * Produces a descriptive error message if the constraint is not met.
 *
 * Uses `OverlayResult` to extract the close event argument from the component's emit type.
 * If no close emit exists, it resolves to `never`.
 *
 * Components without explicit emit definitions have `ComponentEmit<T>` resolve to `{}`
 * (empty object), which causes `OverlayResult` to return `any`.
 */
type HasCloseEmit<T extends Component> = true extends IsAny<OverlayResult<T>>
	? "The component must define a 'close' emit event"
	: [OverlayResult<T>] extends [never]
			? "The component must define a 'close' emit event"
			: T;

interface ShowConfirmationDialogOptions<T extends Component = typeof DDialog> {
	component?: HasCloseEmit<T>;
	props?: ComponentProps<T>;
	onConfirm?: (result: ConfirmPayload<T>) => void;
	onCancel?: () => void;
}

export async function showConfirmationDialog<T extends Component = typeof DDialog>(
	options: ShowConfirmationDialogOptions<T>,
) {
	const { component = DDialog, props, onConfirm, onCancel } = options;
	const modal = useOverlay().create(component as T, { props });

	const instance = modal.open();
	const result = await instance.result;

	if (result) {
		onConfirm?.(result as ConfirmPayload<T>);
	}
	else {
		onCancel?.();
	}

	return result;
}

/**
 * This is an unused function that purely exists for extracting the correct return type of the opened modal
 */
function _inferOverlayResult<T extends Component>(component: T) {
	const overlay = useOverlay().create(component);
	return overlay.open();
}
